import Group from 'components/utils/Group'
import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import BundleOptionTileGroup from './BundleAndSaveTiles/BundleOptionTileGroup'
import {
  BundleOfferPackageOptionView,
  getAvailableBundlePackages,
  getBundledOffersAvailableRates,
  areBundlePricesLoading,
  getBundledPackages,
} from 'selectors/offerPage/bundledOfferPageSelectors'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import BodyText from 'components/Luxkit/Typography/BodyText'
import SupportNumberButton from 'components/Luxkit/Button/SupportNumberButton'
import { buildBundlePackagesViewOrder } from 'lib/bundleAndSave/buildBundlePackagesViewOrder'
import BundleAndSaveCallUs from './BundleAndSaveCallUs'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import useHasGotToAnchor from 'hooks/useHasGotToAnchor'
import { EmptyArray } from 'lib/array/arrayUtils'

const ConstrainedContent = styled.div`
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
`

interface Props {
  filteredBundlePackageId?: string
  packageOptions: { [bundlePackageId: string]: BundleOfferPackageOptionView }
  bundledPackages: { [bundlePackageId: string]: Array<App.HotelPackage> }
  offersAvailableRates?: Array<App.OfferAvailableRates>
  fetching?: boolean;
  availableForPurchase: boolean;
  onStickyFooterToggle: (visible: boolean) => void;
}

function BundleAndSaveOptionsSection({
  filteredBundlePackageId,
  packageOptions,
  bundledPackages,
  offersAvailableRates,
  fetching,
  availableForPurchase,
  onStickyFooterToggle,
}: Props) {
  const hasRateError = (offersAvailableRates ?? []).some((p) => !!p?.error)

  const filteredPackageOptions = useMemo(() => buildBundlePackagesViewOrder({
    filteredBundlePackageId,
    packageOptions,
    bundledPackages,
    fetching,
    availableForPurchase,
  }), [filteredBundlePackageId, packageOptions, bundledPackages, fetching, availableForPurchase])

  const packageStartRef = useRef<HTMLDivElement>(null)
  const reachedAnchor = useHasGotToAnchor(packageStartRef?.current, 0, 25)
  useEffect(() => {
    onStickyFooterToggle(!reachedAnchor)
  }, [onStickyFooterToggle, reachedAnchor])
  return (
    <ConstrainedContent>
      <Group direction="vertical" gap={20}>
        {availableForPurchase && (
          <Group fullWidth direction="vertical" horizontalAlign="center" gap={12}>
            <BodyText variant="large">Select a bundle type</BodyText>
          </Group>
        )}
        {hasRateError && <MessageBanner
          kind="warning"
          heading="The reservation system is temporarily down"
          description="Our team are working on it now. Please check back later or call customer service anytime, 24 hours, 7 days a week."
          trailingContent={<SupportNumberButton />}
        />}
        {!hasRateError && (
          <Group direction="vertical" gap={32} ref={packageStartRef}>
            {filteredPackageOptions.map((bundlePackageId) => <BundleOptionTileGroup
              key={bundlePackageId}
              bundledPackages={bundledPackages[bundlePackageId] ?? EmptyArray}
              packageView={packageOptions[bundlePackageId]}
              fetching={fetching}
              availableForPurchase={availableForPurchase}
            />)}
          </Group>
        )}

        {isBundleAndSaveV2Enabled() && <BundleAndSaveCallUs />}
      </Group>
    </ConstrainedContent>
  )
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    filteredBundlePackageId: !state.activeFilter ? state.filteredBundlePackageId : undefined,
    bundledPackages: getBundledPackages(state),
    packageOptions: getAvailableBundlePackages(state, appState),
    offersAvailableRates: getBundledOffersAvailableRates(appState, state),
    fetching: areBundlePricesLoading(appState, state),
  }
}, BundleAndSaveOptionsSection)
